<template>
  <div class="login-container">
    <div class="login-box">
      <div class="login-header">
        <h1 class="login-title">{{vm.projectName}}</h1>
        <p class="login-subtitle">{{ vm.projectNameEn }}</p>
      </div>

      <el-form
        ref="loginFormRef"
        :model="loginForm"
        :rules="loginRules"
        class="login-form"
        @submit.prevent="handleLogin"
      >
        <el-form-item prop="username">
          <el-input
            v-model="loginForm.username"
            placeholder="请输入用户名"
            size="large"
            clearable
          >
            <template #prefix>
              <el-icon><User /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="password">
          <el-input
            v-model="loginForm.password"
            type="password"
            placeholder="请输入密码"
            size="large"
            show-password
            clearable
            @keyup.enter="handleLogin"
          >
            <template #prefix>
              <el-icon><Lock /></el-icon>
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <!-- <el-checkbox v-model="loginForm.remember">记住密码</el-checkbox> -->
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            size="large"
            :loading="loading"
            class="login-button"
            @click="handleLogin"
          >
            {{ loading ? '登录中...' : '登录' }}
          </el-button>
        </el-form-item>
      </el-form>

      <div class="login-footer">
        <p>copyright: - -</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { User, Lock } from '@element-plus/icons-vue';
import type { FormInstance, FormRules } from 'element-plus';
import { apiPost, apiPostInit, setCookie, TApiConfig } from 'dlibs';
import { EApi } from '@/webmanage/ts/utils/data_enum';
import { toast } from '@/webmanage/ts/utils/tools';
import { $api } from '@/common/api';
import { isDev, toIndexPage } from '@/common/public';

// 页面变量
const vm = reactive({
  projectName: '',
  projectNameEn: '',
})
// 表单引用
const loginFormRef = ref<FormInstance>();

// 加载状态
const loading = ref(false);

// 登录表单数据
const loginForm = reactive({
  username: '',
  password: '',
  remember: false,
});

// 表单验证规则
const loginRules: FormRules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' },
    { min: 2, max: 20, message: '用户名长度在 2 到 20 个字符', trigger: 'blur' },
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 4, max: 20, message: '密码长度在 4 到 20 个字符', trigger: 'blur' },
  ],
};

// 登录处理
const handleLogin = async () => {
  if (!loginFormRef.value) return;

  try {
    // 验证表单
    await loginFormRef.value.validate();

    loading.value = true;

    const res = await $api(EApi.login, loginForm, () => {}, false);
    toast.success(res.msg);
    setCookie('token', res.data.token);
    setCookie('userInfo', JSON.stringify(res.data.user));
    if(isDev()){
      window.location.href = '/vue3/webmanage/ts/pages/index/index/index';
    }else{
      window.location.href = '/webmanage/index/index';
    }
  } catch (error) {
    console.error('登录失败:', error);
    if (error !== false) {
      ElMessage.error('登录失败，请重试');
    }
  } finally {
    loading.value = false;
  }
};

// 判断如果是登录状态就去首页
const checkLogin = async () => {
  const res = await $api(EApi.isLogin, {});
  if(res.code == 1 && res.data){
    return toIndexPage();
  }else{
    getWebOpenData();
  }
}

// 获取项目的基础信息
const getWebOpenData = async () => {
  const res = await $api(EApi.getWebOpenData, {});
  if(res.code == 1){
    const data = res.data;
    vm.projectName = data?.pro_name || '';
    vm.projectNameEn = data?.pro_name_en || '';
  }
}
onMounted(() => {
  checkLogin();
})
</script>

<style scoped lang="scss">
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0 20px;
}

.login-box {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 40px;
}

.login-header {
  text-align: center;
  margin-bottom: 40px;

  .login-title {
    font-size: 28px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px 0;
  }

  .login-subtitle {
    font-size: 14px;
    color: #999;
    margin: 0;
  }
}

.login-form {
  .login-button {
    width: 100%;
    height: 44px;
    font-size: 16px;
    font-weight: 500;
  }
}

.login-footer {
  text-align: center;
  margin-top: 20px;

  p {
    font-size: 13px;
    color: #999;
    margin: 0;
  }
}

// 响应式设计
@media (max-width: 480px) {
  .login-box {
    padding: 30px 20px;
  }

  .login-header {
    .login-title {
      font-size: 24px;
    }
  }
}
</style>
